import { TableColumnGroup, TableColumnVisibilityControllerV2 } from "../../shared/scripts/table-column-visibility";
import { DoArraysOverlap } from "../../shared/scripts/array-functions";
import {GetSelectedDropdownValues, IFilter, InitializeFilterDropdown,InitializeFilterOptGroupDropdown, DropdownOptGroup  } from "../../shared/scripts/dropdown-functions";
import { getDisplayDate } from "../../shared/scripts/date-functions";



interface AnimalModelsPageVm {
    Items: AnimalModelVm[];
   
}
interface FiltersVm {
    Purposes: string[];
    VirusFamilies: string[];
    ChallengeViruses: string[];
    Routes: string[];
    Absl: string[];
    Species: string[];
    Strains: string[];
    Sexes: string[];
    Endpoints: string[];
    Centers: string[];
    ModelStatuses: string[];
    Projects: string[];
    Cores: string[];
    ModelChampions: string[];        

}

interface AnimalModelVm {
    Id: number;
    ModelName: string;
    ModelPurposes: string[];
    Virus: VirusVm;
    Animal: AnimalVm;
    Developer: DeveloperVm;
    LastUpdated?: string | null; 
}
interface VirusVm {
    Center: string;
    VirusFamily: string;
    ChallengeViruses: string[];
    Route: string[];
    VirusStrainDetails: string;
    VirusSource: string;
    VirusCharacterization: string;
    VirusDose: string;
}
interface AnimalVm {
    Center: string;
    Species: string;
    Strains: string[];
    StrainDetails: string;
    Sources: string[];
    Absl: string;
    Sexes: string[];
    Age: string;
    Weight: string;
    Endpoints: string[];
    EndpointDetails: string;
}
interface DeveloperVm {
    Center: string;
    Cores: string[];
    Projects: string[];
    Champion: string;
    // optional champion fields (add to Modal)
    ChampionEmail: string;
    ChampionLinkedinLink: string;
    ChampionOrganizations: string[];
    ModelStatus: string;
    PositiveControl: string;
    ProtocolUrl: string;
    Publications: string[];
    AdditionalInfo: string;
}

export class AnimalModelsPage {
    private models: AnimalModelVm[] = [];
    // abort in-flight detail request if user clicks another row
   
    private virusCtrl?: TableColumnVisibilityControllerV2;
    private animalCtrl?: TableColumnVisibilityControllerV2;
    private developerCtrl?: TableColumnVisibilityControllerV2;

    // DataTable handles
    private dtVirus?: DataTables.Api;
    private dtAnimal?: DataTables.Api;
    private dtDeveloper?: DataTables.Api;

    // Build an index once
  
    // references to installed search callbacks so can replace them cleanly
    private virusSearchFn?: (settings: any, data: any[], dataIndex: number) => boolean;
    private animalSearchFn?: (settings: any, data: any[], dataIndex: number) => boolean;
    private developerSearchFn?: (settings: any, data: any[], dataIndex: number) => boolean;

    // Accepts either a JSON string of AnimalModelsPageVm or the object itself
    constructor(animalModelsInput: string | AnimalModelsPageVm) {
        const parsed = this.parseInput(animalModelsInput);
        this.models = parsed.items;

        this.buildIndex(this.models); 

        this.initTables();

        this.wireRowActions(); // delegates .pdf-btn and .model-name-link clicks for all 3 tables

        this.wireTabSwitching();
        this.hookUpToolbar();
       


        //Static page functionality calls
        this.init();
        this.toggleVisibility();
        this.setupScrollHighlight();
        this.initAccordion();
        this.addResizeListener();
    }
    //Data check
    private parseInput(input: string | AnimalModelsPageVm): { items: AnimalModelVm[] } {
        if (Array.isArray(input as any)) return { items: input as unknown as AnimalModelVm[] };
        if (typeof input === "string") {
            try {
                const parsed = JSON.parse(input);
                if (Array.isArray(parsed)) return { items: parsed as AnimalModelVm[] };
                if (parsed && Array.isArray(parsed.Items)) return { items: parsed.Items as AnimalModelVm[] };
            } catch (e) { console.error("Failed to parse AnimalModelsJson", e); }
            return { items: [] };
        }
        return { items: input.Items };
    }

    // Helpers for filters mapping:
    //S = scalar selector,
    //M = multi - value selector(arrays)
    // Sx/Mx = same, but merge multiple fields

    private S = (sel: (m: AnimalModelVm) => string | null | undefined) =>
        this.uniq(this.models.map(sel));

    private M = (sel: (m: AnimalModelVm) => string[] | null | undefined) =>
        this.uniqFlat(this.models.map(sel));

    private Sx = (...sels: Array<(m: AnimalModelVm) => string | null | undefined>) =>
        this.uniq(this.models.flatMap(m => sels.map(s => s(m))));

    private Mx = (...sels: Array<(m: AnimalModelVm) => string[] | null | undefined>) =>
        this.uniqFlat(this.models.flatMap(m => sels.map(s => s(m))));

    private computeFiltersFromModels(): FiltersVm {
        return {
            Purposes: this.M(m => m.ModelPurposes),
            VirusFamilies: this.S(m => m.Virus?.VirusFamily),
            ChallengeViruses: this.M(m => m.Virus?.ChallengeViruses),
            Routes: this.M(m => m.Virus?.Route),
            Absl: this.S(m => m.Animal?.Absl),
            Species: this.S(m => m.Animal?.Species),
            Strains: this.M(m => m.Animal?.Strains),
            Sexes: this.M(m => m.Animal?.Sexes),
            Endpoints: this.M(m => m.Animal?.Endpoints),
            Centers: this.Sx(m => m.Virus?.Center, m => m.Animal?.Center, m => m.Developer?.Center),
            ModelStatuses: this.S(m => m.Developer?.ModelStatus),
            Projects: this.M(m => m.Developer?.Projects),
            Cores: this.M(m => m.Developer?.Cores),
            ModelChampions: this.S(m => m.Developer?.Champion),       
        };
    }
    private getFilters(): FiltersVm { return this.computeFiltersFromModels(); }

    // Grouping of columns for each table 
    private virusGroups(): TableColumnGroup[] {
        return [
            new TableColumnGroup('Virus', true, [], 'col-virus'),
            new TableColumnGroup('Animal', false, [], 'col-animal'),
            new TableColumnGroup('Developer', false, [], 'col-developer'),
        ];
    }
    private animalGroups(): TableColumnGroup[] {
        return [
            new TableColumnGroup('Virus', false, [], 'col-virus'),
            new TableColumnGroup('Animal', true, [], 'col-animal'),
            new TableColumnGroup('Developer', false, [], 'col-developer'),
        ];
    }
    private developerGroups(): TableColumnGroup[] {
        return [
            new TableColumnGroup('Virus', false, [], 'col-virus'),
            new TableColumnGroup('Animal', false, [], 'col-animal'),
            new TableColumnGroup('Developer', true, [], 'col-developer'),
        ];
    }

    private buildHaystack(r: AnimalModelVm): string {
        const flat = (v: any) =>
            Array.isArray(v) ? v.filter(Boolean).map(String) : (v ? [String(v)] : []);
        const parts: string[] = [
            r.ModelName, ...flat(r.ModelPurposes),
            r.Virus?.VirusFamily, ...flat(r.Virus?.ChallengeViruses), ...flat(r.Virus?.Route),
            r.Virus?.VirusStrainDetails, r.Virus?.VirusSource, r.Virus?.VirusCharacterization, r.Virus?.VirusDose,
            r.Animal?.Species, ...flat(r.Animal?.Strains), r.Animal?.Absl, ...flat(r.Animal?.Sexes),
            ...flat(r.Animal?.Endpoints), r.Animal?.StrainDetails, ...flat(r.Animal?.Sources), r.Animal?.Age, r.Animal?.Weight,
            r.Developer?.Center, ...flat(r.Developer?.Projects), ...flat(r.Developer?.Cores),
            r.Developer?.Champion, r.Developer?.ModelStatus,
            // include PositiveControl in search, but not visible in table
            r.Developer?.PositiveControl,
            ...flat(r.Developer?.Publications), r.Developer?.AdditionalInfo
        ].filter(Boolean) as string[];
        return parts.join(' ');
    }

    // Table values shared + required 3 columns + Haystack for search of vm
    private initTables(): void {
        const join = (d?: string[]) => d?.join(', ') ?? '';

        const baseCols: DataTables.ColumnSettings[] = [
            {
                data: 'ModelName',
                title: 'Model Name',
                className: 'model-name-cell table-column-md b-fixed-left',
                render: (data: string, type: string, row: AnimalModelVm) =>
                    type !== 'display'
                        ? data
                        : `<a href="#" class="model-name-link" data-id="${row.Id}" title="View details">${data}</a>`
            },
        ];

        const virusCols: DataTables.ColumnSettings[] = [
            { data: 'ModelPurposes', title: 'Model Purpose', className: 'col-virus table-column-lg', render: join },
            { data: 'Virus.VirusFamily', title: 'Virus Family', className: 'col-virus table-column-sm' },
            { data: 'Virus.ChallengeViruses', title: 'Challenge Virus(es)', className: 'col-virus table-column-md', render: join },
            { data: 'Virus.Route', title: 'Virus Route', className: 'col-virus table-column-sm', render: join },
        ];

        const animalCols: DataTables.ColumnSettings[] = [
            { data: 'Animal.Species', title: 'Species', className: 'col-animal table-column-sm' },
            { data: 'Animal.Strains', title: 'Strain', className: 'col-animal table-column-md', render: join },
            { data: 'Animal.Endpoints', title: 'Endpoint(s)', className: 'col-animal table-column-lg', render: join },
            { data: 'Animal.Sexes', title: 'Sex', className: 'col-animal table-column-sm', render: join },
            { data: 'Animal.Absl', title: 'ABSL', className: 'col-animal table-column-sm' },
        ];

        const developerCols: DataTables.ColumnSettings[] = [
            { data: 'Developer.Center', title: 'Center', className: 'col-developer table-column-sm' },
            {
                data: null,
                title: 'Core/Project',
                className: 'col-developer text-left font-size12 table-column-lg lh-normal',
                render: (_d, type, row: any) => {
                    const toArr = (v: any) =>
                        Array.isArray(v)
                            ? v.map((x: any) => (typeof x === 'string' ? x : (x?.Name ?? x?.name ?? ''))).filter(Boolean)
                            : (typeof v === 'string' ? v.split(',').map(s => s.trim()).filter(Boolean) : []);

                    const cores = toArr(row?.Developer?.Cores);
                    const projects = toArr(row?.Developer?.Projects ?? row?.Developer?.ProjectNames);

                    const items = [...cores, ...projects]; 

                   
                    if (type !== 'display') return items.join(', ');

                    if (items.length === 0) return '';
                    if (items.length === 1) return items[0];

                    
                    return items.map(s => `<span>${s}</span>`).join(`<hr class="my-1">`);
                }
            },

            { data: 'Developer.ModelStatus', title: 'Model Status', className: 'col-developer table-column-md' },
            { data: 'Developer.Champion', title: 'Model Developer', className: 'col-developer table-column-md' },
        ];

        const pdfCol: DataTables.ColumnSettings = {
            data: null,
            title: ' <i class="fa-solid fa-file-pdf" style="font-size:14px;"></i>',
            orderable: false,
            searchable: false,
            className: 'text-center table-column-icon no-export b-fixed-left-2',
            render: (_data, _type, row: AnimalModelVm) =>
                `<button type="button" class="btn btn-sm pdf-btn" data-id="${row.Id}" title="Open Printable View">
       <i class="fa-solid fa-file-pdf"></i>
     </button>`
        };



        // One hidden, searchable column with everything for search to match
        const searchHaystackCol: DataTables.ColumnSettings = {
            data: null,
            title: '',
            className: 'col-search-haystack no-export',
            visible: false,       // hidden from UI
            searchable: true,     // but included in global search
            orderable: false,
            render: (_d, _t, row: AnimalModelVm) => this.buildHaystack(row)
        };

        // Virus (60vh default)
        {
            const cols = [...baseCols, pdfCol, ...virusCols, ...animalCols, ...developerCols,searchHaystackCol];
            const { dt, ctrl } = this.initOneTable('virus', '#virusTable', cols, this.virusGroups(), 'animal-models-virus');
            this.dtVirus = dt; this.virusCtrl = ctrl;
        }

        // Animal (override scrollY)
        {
            const cols = [...baseCols, pdfCol, ...virusCols, ...animalCols, ...developerCols,  searchHaystackCol];
            const { dt, ctrl } = this.initOneTable(
                'animal',
                '#animalTable',
                cols,
                this.animalGroups(),
                'animal-models-animal',
                { scrollY: '50vh' }
            );
            this.dtAnimal = dt; this.animalCtrl = ctrl;
        }

        // Developer (override scrollY)
        {
            const cols = [...baseCols, pdfCol, ...virusCols, ...animalCols, ...developerCols, searchHaystackCol];
            const { dt, ctrl } = this.initOneTable(
                'developer',
                '#developerTable',
                cols,
                this.developerGroups(),
                'animal-models-developer',
                { scrollY: '50vh' }
            );
            this.dtDeveloper = dt; this.developerCtrl = ctrl;
        }

        // One pass to adjust all columns
        setTimeout(() => {
            try { ($('#virusTable').DataTable() as any).columns.adjust().draw(false); } catch { }
            try { ($('#animalTable').DataTable() as any).columns.adjust().draw(false); } catch { }
            try { ($('#developerTable').DataTable() as any).columns.adjust().draw(false); } catch { }
        }, 0);
    }
  

    // Shared Table logic
    private initOneTable(
        prefix: 'virus' | 'animal' | 'developer',
        tableSelector: '#virusTable' | '#animalTable' | '#developerTable',
        columns: DataTables.ColumnSettings[],
        groups: TableColumnGroup[],
        uniqueKey: string,
        extra: Partial<DataTables.Settings> = {}
    ) {
        const commonOpts: DataTables.Settings = {
            dom: '',
            pageLength: 25,
            autoWidth: true,
            info: false,
            paging: false,
            searching: true,
            orderCellsTop: true,
            scrollX: true,
            scrollY: '60vh',
            scrollCollapse: true,
            buttons: [{
                text: '<i class="fas fa-file-download"></i>',
                titleAttr: 'CSV',
                action: (_e, dt) => this.exportCsvForVisibleModels(dt as DataTables.Api, `animal-models_${prefix}`)
            }],
            columnDefs: [{ targets: '_all', defaultContent: '' }],
            ...extra
        };

        const styleTopControls = (p: string) => {
            $(`.${p}-top-controls`).addClass('row mx-0 align-items-center');
            $(`.${p}-column-select`).addClass('col-12 col-md-3 px-0 mb-2')
                .html(`<div id="${p}-column-select"></div>`);
            $(`.${p}-search-bar`).addClass('col-12 col-md-3');
            $(`.${p}-count-found`).addClass('col-12 col-md-6 d-flex justify-content-end vertical-align-middle mt-3 px-0');
        };

        const dt = $(tableSelector).DataTable({
            ...commonOpts,
            dom: `<"${prefix}-top-controls"<"${prefix}-column-select"><"${prefix}-search-bar"f><"${prefix}-count-found"B>>rtip`,
            data: this.models,
            columns,
            fixedColumns: { leftColumns: 2 }
        });

        // Add the Filter Definitions button (once)

        styleTopControls(prefix);
        dt.on('draw.dt', () => this.updateCountForActiveTab());

        const ctrl = new TableColumnVisibilityControllerV2(
            tableSelector,
            `#${prefix}-column-select`,
            groups,
            uniqueKey
        );

        return { dt, ctrl };
    }


    private exportCsvForVisibleModels(dt: DataTables.Api, baseName: string) {
        const visible = dt.rows({ search: 'applied', page: 'all' }).data().toArray() as AnimalModelVm[];
        const idSet = new Set(visible.map(m => m.Id));
        const rows = this.models.filter(m => idSet.has(m.Id));

        const H = [
             'Model Name', 'Model Purposes',
            'Virus Family', 'Challenge Viruses', 'Route', 'Virus Strain Details', 'Virus Source', 'Virus Characterization', 'Dose',
            'Species', 'Strains', 'ABSL', 'Sex(es)', 'Endpoint(s)', 'Strain Details', 'Sources', 'Age', 'Weight',
            'Developer Center', 'Cores', 'Projects', 'Model Developer', 'Champion Email', 'Champion LinkedIn', 'Champion Organizations',
            'Model Status', 'Positive Control', 'Publications', 'Additional Info', 'Last Updated'
        ];
        const L = (v?: string[] | null) => (v ?? []).filter(Boolean).join('; ');
        const V = (v?: string | null) => (v ?? '');

        const data = rows.map(m => [
            V(m.ModelName), L(m.ModelPurposes),
            V(m.Virus?.VirusFamily), L(m.Virus?.ChallengeViruses), L(m.Virus?.Route), V(m.Virus?.VirusStrainDetails),
            V(m.Virus?.VirusSource), V(m.Virus?.VirusCharacterization), V(m.Virus?.VirusDose),
            V(m.Animal?.Species), L(m.Animal?.Strains), V(m.Animal?.Absl), L(m.Animal?.Sexes), L(m.Animal?.Endpoints),
            V(m.Animal?.StrainDetails), L(m.Animal?.Sources), V(m.Animal?.Age), V(m.Animal?.Weight),
            V(m.Developer?.Center), L(m.Developer?.Cores), L(m.Developer?.Projects), V(m.Developer?.Champion),
            V(m.Developer?.ChampionEmail), V(m.Developer?.ChampionLinkedinLink), L(m.Developer?.ChampionOrganizations),
            V(m.Developer?.ModelStatus), V(m.Developer?.PositiveControl),
            L(m.Developer?.Publications), V(m.Developer?.AdditionalInfo), V(m.LastUpdated)
        ]);

        const csv = this.buildCsv(H, data);
        this.downloadCsv(csv, `${baseName}_all-fields_${new Date().toISOString().slice(0, 10)}.csv`);
    }

    private buildCsv(headers: string[], rows: string[][]) {
        const esc = (s: any) => `"${String(s).replace(/"/g, '""')}"`;
        return '\uFEFF' + [headers.map(esc).join(','), ...rows.map(r => r.map(esc).join(','))].join('\r\n');
    }

    private downloadCsv(csv: string, filename: string) {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(a.href);
        document.body.removeChild(a);
    }

    // Search for text for table
    private toArr(v?: string | string[] | null): string[] {
        if (!v) return [];
        return Array.isArray(v) ? v : [v];
    }

    // Index helper for WireRowActions
    private modelIndex = new Map<number, AnimalModelVm>();
    private buildIndex(items: AnimalModelVm[]): void {
        this.modelIndex.clear();
        items.forEach(m => this.modelIndex.set(m.Id, m));
    }

    // Click function for row and index check per row
    private wireRowActions(): void {
        const $root = $('#inventory');

        $root.off('click', 'a.model-name-link').on('click', 'a.model-name-link', (e) => {
            e.preventDefault();
            const id = Number((e.currentTarget as HTMLElement).getAttribute('data-id') || '0');
            const model = this.modelIndex.get(id);
            const $modal = $('#animal-model-detail-modal');

            if (!model) {
                $('#animal-model-detail-modal-body').html('<div class="p-4 text-danger">Model not found.</div>');
                $modal.modal('show');
                return;
            }

            // Reset tabs to first
            $modal.find('.nav-link').removeClass('active');
            $modal.find('.tab-pane').removeClass('show active');
            $modal.find('a[href="#am-tab-virus"]').addClass('active');
            $('#am-tab-virus').addClass('show active');

            // Fill Modal ****
            this.fillModal(model);
            $modal.modal('show');
        });

        //PDF clck function---
        $(document).off('click', '.pdf-btn').on('click', '.pdf-btn', (e) => {
            const id = Number((e.currentTarget as HTMLElement).getAttribute('data-id') || '0');
            const model = this.modelIndex.get(id);
            if (!model) { alert('Sorry, model not found.'); return; }
            this.printNow(model);   // <-- one click = native print dialog
        });

    }

    private buildPrintableHtml(m: AnimalModelVm): string {
        const $template = $('#am-print-template #am-print-root');
        const $clone = $template.clone(false, false).addClass('animal-models-hub');
        this.fillInto($clone, m);
        // right after: this.fillInto($clone, m);
        $clone.find('[data-hide-when-empty]').filter(function () {
            const $el = $(this);
            const hasText = $el.text().trim().length > 0;
            const hasStuff = $el.find('img,svg,video,iframe,.pill,[data-has-content="true"]').length > 0;
            return !hasText && !hasStuff;
        }).each(function () {
            const $el = $(this);
            const wrap = $el.attr('data-hide-closest');
            wrap ? $el.closest(wrap).remove() : $el.remove();
        });

        // drop a dangling divider at the very end
        $clone.find('.grey-inner-line:last-child').remove();


        const headAssets = this.collectHeadAssetsHtml();
        const fileBase = this.buildPdfFileName(m);  

        const printOverrides = `
<style>
  @media print {
    /* Keep colors when printing */
    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }

    /* Bootstrap-like grid in print (keeps your 3x col-md-4 rows aligned) */
    .animal-models-hub .row {
      display: flex;
      flex-wrap: wrap;
      margin-left: -15px;
      margin-right: -15px;
    }
    .animal-models-hub [class^="col-"],
    .animal-models-hub [class*=" col-"] {
      padding-left: 15px;
      padding-right: 15px;
      box-sizing: border-box;
    }
    .animal-models-hub .col-md-4 { flex: 0 0 33.3333%; max-width: 33.3333%; }
    .animal-models-hub .col-md-6 { flex: 0 0 50%;       max-width: 50%; }
    .animal-models-hub .col-md   { flex: 1 0 0%;        max-width: 100%; }
    .animal-models-hub .col-md-auto { flex: 0 0 auto; width: auto; }

    /* Auto pair: left can't go below 33.33%, can grow; right shrinks to fit remainder */
    .animal-models-hub .am-auto-pair {
      display: flex;
      flex-wrap: nowrap;
      margin-left: -15px;   /* gutter compensation */
      margin-right: -15px;
    }
    .animal-models-hub .am-auto-pair > .am-left,
    .animal-models-hub .am-auto-pair > .am-right {
      padding-left: 15px;   /* gutters */
      padding-right: 15px;
      box-sizing: border-box;
    }
    .animal-models-hub .am-auto-pair > .am-left {
      flex: 0 1 auto !important;      /* width follows content */
      min-width: 33.3333% !important; /* never below col-4 */
      max-width: 50%;
      overflow-wrap: anywhere;
      word-break: break-word;
    }
    .animal-models-hub .am-auto-pair > .am-right {
      flex: 1 1 0% !important;        /* fill remainder, allow shrinking */
      min-width: 0;                    /* IMPORTANT so it can actually shrink */
      overflow-wrap: anywhere;
      word-break: break-word;
    }

   
    .animal-models-hub .overview-blue-card { max-width: 300px; }

    /* Animal table header background + borders */
    .animal-models-hub .am-animal-table { border-collapse: collapse; }
    .animal-models-hub .am-animal-table thead th {
      background-color: #b1e2f2 !important;  /* light blue */
      color: #0f222f !important;
      border: 1px solid #0f222f !important;
    }
    .animal-models-hub .am-animal-table td {
      border: 1px solid #0f222f !important;
    }
    .animal-models-hub .am-animal-table th,
    .animal-models-hub .am-animal-table td {
      display: table-cell !important;
    }

    /* Section dividers in print */
    .animal-models-hub .blue-line { background: #007bff !important; height: 2px; }
    .animal-models-hub .grey-inner-line { background: #e9ecef !important; height: 1px; }
   
  .animal-models-hub .d-none,[hidden]{display:none!important;}
  .animal-models-hub .grey-inner-line:last-child{display:none!important;}
  .animal-models-hub > :last-child{margin-bottom:0!important;padding-bottom:0!important;}
  .animal-models-hub dd.as-pills[data-am="Virus.ChallengeViruses"]{display:flex!important;flex-direction:row!important;flex-wrap:wrap!important}


  }


</style>`;



        return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>${fileBase}</title> 
  <base href="${document.baseURI}">
  ${headAssets}
  ${printOverrides}
  <style>body{background:#fff;padding:16px}@media print{body{padding:0}}</style>
</head>
<body>${$clone.prop('outerHTML')}</body>
</html>`;

    }

    //Get ReVAMPP image for pdf
    private async waitForImages(doc: Document): Promise<void> {
        const imgs = Array.from(doc.images || []);
        await Promise.all(imgs.map(img => {
            if (img.complete) return Promise.resolve();
            return new Promise<void>(res => { img.onload = () => res(); img.onerror = () => res(); });
        }));
    }
    private async printNow(m: AnimalModelVm): Promise<void> {
        const html = this.buildPrintableHtml(m);

        const frame = document.createElement('iframe');
        frame.style.position = 'fixed';
        frame.style.right = '0';
        frame.style.bottom = '0';
        frame.style.width = '0';
        frame.style.height = '0';
        frame.style.border = '0';
        document.body.appendChild(frame);

        const cleanup = () => { try { document.body.removeChild(frame); } catch { } };

        frame.onload = async () => {
            const w = frame.contentWindow;
            const d = frame.contentDocument || w?.document;
            if (!w || !d) { cleanup(); return; }

            // Also set the iframe doc title
            const desiredTitle = this.buildPdfFileName(m);
            try { d.title = desiredTitle; } catch { }

            // Wait for fonts/images
            try { if ((d as any).fonts?.ready) await (d as any).fonts.ready; } catch { }
            try { await this.waitForImages(d); } catch { }

            // Temporarily set parent title so Chrome uses it for the PDF filename
            const oldParentTitle = document.title;
            document.title = desiredTitle;

            const restore = () => { document.title = oldParentTitle; cleanup(); };

            setTimeout(() => {
                try {
                    w.focus();
                    w.print();
                    if ('onafterprint' in w) {
                        w.onafterprint = restore;
                    } else {
                        setTimeout(restore, 1500);
                    }
                } catch {
                    restore();
                }
            }, 50);
        };

        if ('srcdoc' in frame) {
            (frame as any).srcdoc = html;
        } else {
            const w = frame.contentWindow;
            const d = frame.contentDocument || w?.document;
            if (d) { d.open(); d.write(html); d.close(); }
        }
    }



    /** Copy page CSS into the print */
    private collectHeadAssetsHtml(): string {
        const pick = (sel: string) => Array.from(document.querySelectorAll(sel));

        // Everything already loaded on the main page
        const pageLinks = ([
            ...pick('link[rel~="stylesheet"]'),
            ...pick('link[rel="preload"][as="style"]')
        ] as HTMLLinkElement[]).map(l => l.href);
      
        // Unique + ordered
        const hrefs = Array.from(new Set([ ...pageLinks]));

        // Any inline <style> blocks on the page
        const inlineStyles = pick('style') as HTMLStyleElement[];


        return [
            ...hrefs.map(href => `<link rel="stylesheet" href="${href}">`),
            ...inlineStyles.map(s => s.outerHTML)
            
        ].join('\n');
    }

    private buildPdfFileName(m: AnimalModelVm): string {
        const name = (m.ModelName || 'Model').trim();
        const raw = `ReVAMPP - ${name}`;              //ReVAMPP - Animal Model name
        return raw.replace(/[\\/:*?"<>|]+/g, '')      // strip illegal filename chars
            .replace(/\s+/g, ' ')
            .trim()
            .slice(0, 180);
    }
   

    // Modal data fill---------------------------------
    private fillModal(m: AnimalModelVm): void {
        const $modalRoot = $('#animal-model-detail-modal');
        this.fillInto($modalRoot, m);
        // ensure modal header button has the id for reuse
        $modalRoot.find('#pdf-btn').html(
            `<button type="button" class="btn btn-sm pdf-btn" data-id="${m.Id}" title="Open Printable View">
       <i class="fa-solid fa-file-pdf"></i>
     </button>`
        );
    }

    // FIll/mapping logic for the modal and the print---------------------------------
    private fillInto($root: JQuery, m: AnimalModelVm): void {
        const set = (path: string, v?: string | string[] | null) => {
            const $hosts = $root.find(`[data-am="${path}"]`);

            $hosts.each((_, el) => {
                const $host = $(el);
                const wantsPills = $host.hasClass('as-pills');
                const hideWhenEmpty = $host.is('[data-hide-when-empty]');
                const hideClosestSel = $host.attr('data-hide-closest') || '';
                const $target = hideClosestSel ? $host.closest(hideClosestSel) : $host;
                const isAnimalCell = $host.closest('table.am-animal-table').length > 0;

                const applyHide = (empty: boolean) => {
                    if (!hideWhenEmpty) return;
                    $target.toggleClass('d-none', empty);
                    if (empty) $target.attr('aria-hidden', 'true');
                    else $target.removeAttr('aria-hidden');
                };

                const toItems = (val?: string | string[] | null): string[] =>
                    Array.isArray(val)
                        ? val.map(x => String(x).trim()).filter(Boolean)
                        : (typeof val === 'string' && val.trim() ? [val.trim()] : []);

                // ----- PLAIN TEXT RENDER -----
                const renderPlain = (text: string) => {
                    const empty = !text;
                    applyHide(empty);
                    if (empty) {
                        if (isAnimalCell) $host.text('--'); else $host.empty();
                    } else {
                        $host.text(text);
                    }
                };

                // ----- PILL RENDER -----
                const renderPills = (items: string[]) => {
                    const pillClass = ($host.attr('data-pill-class') || 'pill').trim();
                    const iconClass = ($host.attr('data-pill-icon') || '').trim();

                    $host.empty();
                    if (!items.length) { if (isAnimalCell) $host.text('-'); else applyHide(true); return; }
                    applyHide(false);

                    for (const item of items) {
                        const $pill = $('<span/>', { class: pillClass });
                        if (iconClass) $('<i/>', { class: iconClass, style: 'margin-right:5px;' }).appendTo($pill);

                        if (path === 'Developer.Publications') {
                            const pmid = String(item).match(/\d+/)?.[0] || '';
                            $('<strong/>', { text: 'PubMed Id : ' }).appendTo($pill);
                            if (pmid) {
                                $('<a/>', {
                                    href: `https://pubmed.ncbi.nlm.nih.gov/${pmid}/`,
                                    target: '_blank', rel: 'noopener', text: pmid, class: 'pill-link external'
                                }).appendTo($pill);
                            } else {
                                $('<span/>', { text: String(item).trim() }).appendTo($pill);
                            }
                        } else {
                            $('<span/>', { text: item }).appendTo($pill);
                        }

                        $pill.appendTo($host);
                    }
                };

                

                // ---------- MAIN ----------
                if (wantsPills) {
                    let items = toItems(v);

                    // SPECIAL: Virus Dose -> no splitting; wrap as plain text if long
                    if (path === 'Virus.VirusDose') {
                        const raw = Array.isArray(v) ? items.join(', ') : (typeof v === 'string' ? v.trim() : '');
                        if (!raw) { if (isAnimalCell) $host.text('-'); else applyHide(true); return; }

                        const limitAttr = parseInt($host.attr('data-pill-max-chars') || '20', 10);
                        const limit = Number.isFinite(limitAttr) ? limitAttr : 20;

                        if (raw.length > limit) {
                            $host.addClass('wrap-anywhere').text(raw); // plain wrapped text, no pill
                            return;
                        }
                        items = [raw]; // short = single pill (dose)
                    }

                    // DEFAULT: render pills
                    renderPills(items);
                    return;
                }

                // Non-pill fields
                if (Array.isArray(v)) {
                    const arr = toItems(v);
                    renderPlain(arr.join(', '));
                } else {
                    renderPlain(typeof v === 'string' ? v.trim() : '');
                }
            });
        };

        // ------- Header -------
        const hasPurposes = Array.isArray(m.ModelPurposes) && m.ModelPurposes.length > 0;
        $root.find('#am-purpose-row').toggleClass('d-none', !hasPurposes);

        set('ModelPurposes', m.ModelPurposes ?? []);
        set('ModelName', m.ModelName);
        const center = m.Developer?.Center || m.Animal?.Center || m.Virus?.Center || '—';
        set('Center', center);
        set('Developer.ModelStatus', m.Developer?.ModelStatus);

        $root.find('#am-last-updated').text(
            m.LastUpdated ? `Last Updated: ${getDisplayDate(new Date(m.LastUpdated))}` : ''
        );

        // ------- Virus -------
        set('Virus.VirusFamily', m.Virus?.VirusFamily);
        set('Virus.VirusDose', m.Virus?.VirusDose);
        set('Virus.Route', m.Virus?.Route);
        set('Virus.ChallengeViruses', m.Virus?.ChallengeViruses);
        set('Virus.VirusStrainDetails', m.Virus?.VirusStrainDetails);
        set('Virus.VirusSource', m.Virus?.VirusSource);
        set('Virus.VirusCharacterization', m.Virus?.VirusCharacterization);

        // ------- Animal -------
        set('Animal.Species', m.Animal?.Species);
        set('Animal.Strains', m.Animal?.Strains);
        set('Animal.Age', m.Animal?.Age);
        set('Animal.Weight', m.Animal?.Weight);
        set('Animal.Sources', m.Animal?.Sources);
        set('Animal.Absl', m.Animal?.Absl);
        set('Animal.Sexes', m.Animal?.Sexes);
        set('Animal.StrainDetails', m.Animal?.StrainDetails);
        set('Animal.Endpoints', m.Animal?.Endpoints);
        set('Animal.EndpointDetails', m.Animal?.EndpointDetails);

        // ------- Developer -------
        const $devHost = $root.find('#am-dev-champion-card').empty();
        const $wrap = $devHost.closest('.champ-card');
        const dev = m.Developer;

        // small helpers
        const hideSep = (sel: string) => $root.find(sel).addClass('d-none').attr('aria-hidden', 'true');
        const showSep = (sel: string, show: boolean) =>
            $root.find(sel).toggleClass('d-none', !show).attr('aria-hidden', show ? 'false' : 'true');

        /*if (!dev) {
            $wrap.addClass('d-none');
            ['#dev-sep-top', '#dev-sep-after-protocols', '#dev-sep-after-publications'].forEach(hideSep);
            return;
        }*/

        const name = (dev.Champion || '').trim();
        const orgs = (dev.ChampionOrganizations ?? []).filter(Boolean).join(', ');
        const email = (dev.ChampionEmail || '').trim();
        const linkedin = (dev.ChampionLinkedinLink || '').trim();
        const hasAnyChampion = !!(name || orgs || email || linkedin);

        $wrap.toggleClass('d-none', !hasAnyChampion);
        const hasCores = Array.isArray(dev.Cores) && dev.Cores.filter(Boolean).length > 0;
        const hasTop = hasAnyChampion || hasCores;

        // Look ahead to the sections below the separator
        const hasPositiveControl = !!(dev?.PositiveControl && dev.PositiveControl.trim());
        const hasProtocol = !!dev?.ProtocolUrl; // rendering a button if it exists
        const hasPublications = Array.isArray(dev?.Publications) && dev!.Publications.some(Boolean);
        const hasAdditional = !!(dev?.AdditionalInfo && dev.AdditionalInfo.trim());
        const hasBottom = hasPositiveControl || hasPublications || hasAdditional || hasProtocol;

        // Show separators only when they separate two filled sections
        showSep('#dev-sep-top', hasTop && hasBottom);
        showSep('#dev-sep-after-positive', hasPositiveControl && hasAdditional);
        showSep('#dev-sep-after-publications', hasPublications && hasAdditional);
        showSep('#dev-sep-after-protocols', hasProtocol && hasProtocol);


        if (hasAnyChampion) {
            const $card = $('<div/>', { class: 'overview-blue-card' }).appendTo($devHost);
            if (name) $('<div/>', { class: 'overview-title mt-2', text: name }).appendTo($card);
            if (orgs) $('<div/>', { class: 'overview-subtitle mt-1', text: orgs }).appendTo($card);
            const $icons = $('<div/>', { class: 'row align-items-center mt-1' });
            if (email) $('<div/>', { class: 'col-auto pr-0' })
                .append($('<a/>', { href: `mailto:${email}`, target: '_blank', rel: 'noopener', 'aria-label': 'Email', style: 'color:white;' })
                    .append($('<i/>', { class: 'fa-solid fa-envelope fa-sm' }))).appendTo($icons);
            if (linkedin) $('<div/>', { class: 'col-auto pr-0' })
                .append($('<a/>', { href: linkedin, target: '_blank', rel: 'noopener', 'aria-label': 'LinkedIn', style: 'color:white;' })
                    .append($('<i/>', { class: 'fab fa-linkedin fa-sm' }))).appendTo($icons);
            if ($icons.children().length) $icons.appendTo($card);
        }

        set('Developer.Cores', dev?.Cores);
        set('Developer.Projects', dev?.Projects);
        set('Developer.PositiveControl', dev?.PositiveControl);
        set('Developer.Publications', dev?.Publications);
        set('Developer.AdditionalInfo', dev?.AdditionalInfo);
   
        // --- Protocol inline viewer (simple iframe) ---
        (() => {
            const $slot = $root.find('[data-am="Developer.Protocol"]');
            const hideClosestSel = $slot.attr('data-hide-closest') || '';
            const $target = hideClosestSel ? $slot.closest(hideClosestSel) : $slot;

            $slot.empty();

            const url = m.Developer?.ProtocolUrl?.trim();
            if (!url) {
                $target.addClass('d-none').attr('aria-hidden', 'true');
                return;
            }

            // Use existing clean title
            const pdfTitle = this.buildPdfFileName(m) || 'Protocol';

            const $btn = $('<button/>', {
                type: 'button',
                class: 'btn mb-2 protocol-toggle greenbtn greenbtnsm',
                title: `View ${pdfTitle}`,
                'aria-expanded': 'false',
                html: '<i class="fa-solid fa-file-pdf mr-1" aria-hidden="true"></i> View Protocol'
            });

            const $wrap = $('<div/>', {
                class: 'document-view-body p-0 d-none',
                'aria-hidden': 'true'
            });

            $btn.on('click', () => {
                const isOpen = !$wrap.hasClass('d-none');

                if (isOpen) {
                    // Close: hide and clear iframe
                    $wrap.addClass('d-none').attr('aria-hidden', 'true').empty();
                    $btn
                        .html('<i class="fa-solid fa-file-pdf mr-1" aria-hidden="true"></i> View Protocol')
                        .attr('aria-expanded', 'false')
                        .attr('title', `View ${pdfTitle}`);
                } else {
                    // Open iframe and show
                    const $iframe = $('<iframe/>', {
                        class: 'document-iframe',
                        title: pdfTitle, // clean file name
                        src: url,
                        loading: 'lazy',
                        style: 'width:100%; height:70vh; border:0;'
                    });
                    $wrap.empty().append($iframe).removeClass('d-none').removeAttr('aria-hidden');
                    $btn
                        .html('<i class="fa-solid fa-file-pdf mr-1" aria-hidden="true"></i> Hide Protocol')
                        .attr('aria-expanded', 'true')
                        .attr('title', 'Hide Protocol');
                }
            });

            $slot.append($btn, $wrap);
            $target.removeClass('d-none').removeAttr('aria-hidden');
        })();


    }
    private dtOfActiveTab(): DataTables.Api | undefined {
        const href = $('#list-management-tabs .nav-link.active').attr('href');
        switch (href) {
            case '#virus': return this.dtVirus;
            case '#animal': return this.dtAnimal;
            case '#developer': return this.dtDeveloper;
            default: return undefined;
        }
    }

    private updateCountForActiveTab(): void {
        const dt = this.dtOfActiveTab();
        const count = dt ? dt.rows({ search: 'applied' }).count() : 0;
        $('#am-count').text(`${count} Animal Model${count === 1 ? '' : 's'} Found`);
    }

    private resetAllFiltersForActiveTab(): void {
        // 1) Select ALL options in each filter so logic treats it as "no filter"
        const $c = $('#list-filter-container');
        $c.find('select.selectpicker').each((_i, el) => {
            const $el = $(el);
            if ($el.attr("id") == "sel-dev-core") {
                $el.find("option").prop('selected', false);
            }
            else {
                $el.find('option').prop('selected', true);
            }
            $el.selectpicker('refresh');
        });

        // 2) Clear DT global search and redraw
        const dt = this.dtOfActiveTab();
        if (dt) dt.search('').draw(false);

        // 3) Refresh count
        this.updateCountForActiveTab();
    }

    private hookUpToolbar(): void {
        $('#am-reset').off('click').on('click', () => this.resetAllFiltersForActiveTab());
    }

    private wireTabSwitching(): void {
        $('#list-management-tabs a[data-toggle="tab"]').on('shown.bs.tab', (e) => {
            (['virusTable', 'animalTable', 'developerTable'] as const).forEach(id => {
                try { ($('#' + id).DataTable() as any).columns.adjust().responsive?.recalc?.(); } catch { }
            });

            const target = ($(e.target).attr('href') || '').replace('#', '');
            if (target === 'virus') this.buildVirusFilters();
            else if (target === 'animal') this.buildAnimalFilters();
            else if (target === 'developer') this.buildDeveloperFilters();

            this.updateCountForActiveTab();
        });

        // Build filters immediately for the tab that's already active in the HTML
        const initialHref = $('#list-management-tabs .nav-link.active').attr('href');
        switch (initialHref) {
            case '#virus': this.buildVirusFilters(); break;
            case '#animal': this.buildAnimalFilters(); break;
            case '#developer': this.buildDeveloperFilters(); break;
        }
        this.updateCountForActiveTab();

      
        $('.selectpicker').selectpicker({
            dropupAuto: false,
            selectedTextFormat: 'count',
            countSelectedText: (n, t) => n === t ? 'All' : `${n} Selected`
        });
    }


    // Helpers for filters-----------------------------
    // unique + sorted
    private uniq(values: (string | undefined | null)[]): string[] {
        const set = new Set(values.filter((v): v is string => !!v && v.trim().length > 0));
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }

    // flatten + unique + sorted
    private uniqFlat(listOfArrays: (string[] | undefined | null)[]): string[] {
        const all: string[] = [];
        listOfArrays.forEach(arr => { if (arr) all.push(...arr.filter(x => !!x)); });
        return this.uniq(all);
    }

    private asFilter(dtGetter: () => DataTables.Api | undefined): IFilter {
        return {
            triggerFilter: () => {
                const dt = dtGetter();
                dt?.draw(false);
                this.updateCountForActiveTab();
            }
        };
    }


    private normalizeSelected(selectId: string, selected: string[] | null | undefined): string[] | null {
      if (selected != null) return selected;
      return $(selectId).attr('data-inited') === '1' ? [] : null; // after init: [] means "filter to none"
    }

    private matchesOne(selected: string[] | null | undefined, selectId: string, value?: string): boolean {
      const sel = this.normalizeSelected(selectId, selected);
      if (sel == null) return true;            // not initialized yet
      if (sel.length === 0) return false;      // user cleared all -> match nothing
      if (this.isAllSelected(selectId, sel)) return true;
      return this.toArr(value).some(v => sel.includes(v));
    }

    private matchesAny(selected: string[] | null | undefined, selectId: string, values?: string[]): boolean {
        const sel = this.normalizeSelected(selectId, selected);
        if (sel == null) return true;          
        if (sel.length === 0) return false;    
        if (this.isAllSelected(selectId, sel)) return true; // treat as no filter

        const arr = this.toArr(values);
        if (arr.length === 0) return false;    
        return DoArraysOverlap(arr, sel);
    }


    // is “All” selected? (treats selecting every option as no filter)
    private isAllSelected(selectId: string, selected: string[] | null | undefined): boolean {
        if (!selected) return false;
        const options = document.querySelectorAll<HTMLOptionElement>(`${selectId} option`);
        const unique = new Set(Array.from(options, o => o.value));
        return selected.length === unique.size && unique.size > 0;
    }

    // remove existing DataTables global filter callback
    private removeSearch(fn?: Function) {
        if (!fn) return;
        const arr = $.fn.dataTable.ext.search as Function[];
        const idx = arr.indexOf(fn as any);
        if (idx >= 0) arr.splice(idx, 1);
    }

    //--------------------------------------------------------------------------

    //Building Virus Filters
    private buildVirusFilters(): void {
        // 1) Render dropdowns 
        $('#list-filter-container').html(`
        <div class="row">
          <div class="col-md-3 mb-2">
            <label class="mb-1">Model Purpose</label>
            <select id="sel-virus-purpose" class="selectpicker" multiple data-actions-box="true" data-width="100%" title="None selected"></select>
          </div>
          <div class="col-md-3 mb-2">
            <label class="mb-1">Virus Family</label>
            <select id="sel-virus-family" class="selectpicker" multiple data-actions-box="true" data-width="100%" title="None selected"></select>
          </div>
          <div class="col-md-3 mb-2">
            <label class="mb-1">Challenge Virus(es)</label>
            <select id="sel-virus-challenge" class="selectpicker" multiple data-actions-box="true" data-width="100%" title="None selected" data-live-search="true"></select>
          </div>
          <div class="col-md-3 mb-2">
            <label class="mb-1">Route</label>
            <select id="sel-virus-route" class="selectpicker" multiple data-actions-box="true" data-width="100%" title="None selected"></select>
          </div>
          <div class="col-md-3 mb-2">
            <label class="mb-1">ABSL</label>
            <select id="sel-virus-absl" class="selectpicker" multiple data-actions-box="true" data-width="100%" title="None selected"></select>
          </div>
        </div>
      `);

        
        // NEW Picklists
        const f = this.getFilters();
        const purposes = f.Purposes;
        const families = f.VirusFamilies;
        const challenges = f.ChallengeViruses;
        const routes = f.Routes;
        const absl = f.Absl;

        // 3) Initialize dropdowns
        const handle = this.asFilter(() => this.dtVirus);
        InitializeFilterDropdown('#sel-virus-purpose', purposes, handle);
        InitializeFilterDropdown('#sel-virus-family', families, handle);
        InitializeFilterDropdown('#sel-virus-challenge', challenges, handle);
        InitializeFilterDropdown('#sel-virus-route', routes, handle);
        InitializeFilterDropdown('#sel-virus-absl', absl, handle); // <- NEW

        // 4) (Re)install scoped search
        this.removeSearch(this.virusSearchFn);
        this.virusSearchFn = (settings: any, _data: any[], dataIndex: number) => {
            if (settings.nTable?.id !== 'virusTable') return true;

            const api = new ($.fn.dataTable as any).Api(settings);
            const row = api.row(dataIndex).data() as AnimalModelVm;

            const selPurpose = GetSelectedDropdownValues('#sel-virus-purpose') as string[] | null;
            const selFamily = GetSelectedDropdownValues('#sel-virus-family') as string[] | null;
            const selChallenge = GetSelectedDropdownValues('#sel-virus-challenge') as string[] | null;
            const selRoute = GetSelectedDropdownValues('#sel-virus-route') as string[] | null;
            const selAbsl = GetSelectedDropdownValues('#sel-virus-absl') as string[] | null; // <- NEW

            const okPurpose = this.matchesAny(selPurpose, '#sel-virus-purpose', row.ModelPurposes);
            const okFamily = this.matchesOne(selFamily, '#sel-virus-family', row.Virus?.VirusFamily);
            const okChallenge = this.matchesAny(selChallenge, '#sel-virus-challenge', row.Virus?.ChallengeViruses);
            const okRoute = this.matchesAny(selRoute, '#sel-virus-route', row.Virus?.Route);
            const okAbsl = this.matchesOne(selAbsl, '#sel-virus-absl', row.Animal?.Absl); // <- NEW

            return okPurpose && okFamily && okChallenge && okRoute && okAbsl;
        };
        ($.fn.dataTable.ext.search as any[]).push(this.virusSearchFn);

        // 5) Redraw
        (this.dtVirus as any)?.draw(false);
        this.updateCountForActiveTab();

    }

    //Building Animal Filters-----------------------------
    private buildAnimalFilters(): void {
        $('#list-filter-container').html(`
        <div class="row">
       
          <div class="col-md-3 mb-2">
            <label class="mb-1">Species</label>
            <select id="sel-animal-species" class="selectpicker" multiple data-actions-box="true" data-width="100%"  title="None selected"></select>
          </div>
          <div class="col-md-3 mb-2">
            <label class="mb-1">Strain</label>
            <select id="sel-animal-strain" class="selectpicker" multiple data-actions-box="true" data-width="100%" title="None selected" data-live-search="true"></select>
          </div>
           <div class="col-md-3 mb-2">
            <label class="mb-1">Endpoint(s)</label>
            <select id="sel-animal-endpoint" class="selectpicker" multiple data-actions-box="true" data-width="100%" title="None selected"></select>
          </div>
            <div class="col-md-3 mb-2">
            <label class="mb-1">Sex</label>
            <select id="sel-animal-sex" class="selectpicker" multiple data-actions-box="true" data-width="100%"  title="None selected"></select>
          </div>
           <div class="col-md-3 mb-2">
            <label class="mb-1">ABSL</label>
            <select id="sel-animal-absl" class="selectpicker" multiple data-actions-box="true" data-width="100%"  title="None selected"></select>
          </div>
        </div>
      `);
        const f = this.getFilters();
        const purposes = f.Purposes;
        const species = f.Species;
        const strains = f.Strains;
        const absl = f.Absl;
        const sexes = f.Sexes;
        const endpoints = f.Endpoints;


        const handle = this.asFilter(() => this.dtAnimal);
        InitializeFilterDropdown('#sel-animal-purpose', purposes, handle);
        InitializeFilterDropdown('#sel-animal-species', species, handle);
        InitializeFilterDropdown('#sel-animal-strain', strains, handle);
      
        InitializeFilterDropdown('#sel-animal-sex', sexes, handle);
        InitializeFilterDropdown('#sel-animal-absl', absl, handle);
        InitializeFilterDropdown('#sel-animal-endpoint', endpoints, handle);

        this.removeSearch(this.animalSearchFn);
        this.animalSearchFn = (settings: any, _data: any[], dataIndex: number) => {
            if (settings.nTable?.id !== 'animalTable') return true;

            const api = new ($.fn.dataTable as any).Api(settings);
            const row = api.row(dataIndex).data() as AnimalModelVm;

            const selPurpose = GetSelectedDropdownValues('#sel-animal-purpose') as string[] | null;
            const selSpecies = GetSelectedDropdownValues('#sel-animal-species') as string[] | null;
            const selStrain = GetSelectedDropdownValues('#sel-animal-strain') as string[] | null;
            const selAbsl = GetSelectedDropdownValues('#sel-animal-absl') as string[] | null;
            const selSex = GetSelectedDropdownValues('#sel-animal-sex') as string[] | null;
            const selEndpoint = GetSelectedDropdownValues('#sel-animal-endpoint') as string[] | null;

            const okPurpose = this.matchesAny(selPurpose, '#sel-animal-purpose', row.ModelPurposes);
            const okSpecies = this.matchesOne(selSpecies, '#sel-animal-species', row.Animal?.Species);
            const okStrain = this.matchesAny(selStrain, '#sel-animal-strain', row.Animal?.Strains);
            const okAbsl = this.matchesOne(selAbsl, '#sel-animal-absl', row.Animal?.Absl);
            const okSex = this.matchesAny(selSex, '#sel-animal-sex', row.Animal?.Sexes);
            const okEndpoint = this.matchesAny(selEndpoint, '#sel-animal-endpoint', row.Animal?.Endpoints);

            return okPurpose && okSpecies && okStrain && okAbsl && okSex && okEndpoint;
        };
        ($.fn.dataTable.ext.search as any[]).push(this.animalSearchFn);
        (this.dtAnimal as any)?.draw(false);
        this.updateCountForActiveTab();

    }

    // Building Developer Filters-----------------------------
    private buildDeveloperFilters(): void {
        $('#list-filter-container').html(`
    <div class="row">
      <div class="col-3 mb-2">
        <label class="mb-1">Center</label>
        <select id="sel-dev-center" class="selectpicker" multiple data-actions-box="true" data-width="100%" title="None selected"></select>
      </div>

      <div class="col-3 mb-2">
        <label class="mb-1">Core/Project</label>
        <select id="sel-dev-core" class="selectpicker" multiple data-actions-box="true" data-width="100%" title="None selected"></select>
      </div>

      <div class="col-3 mb-2">
        <label class="mb-1">Model Status</label>
        <select id="sel-dev-status" class="selectpicker" multiple data-actions-box="true" data-width="100%" title="None selected"></select>
      </div>

      <div class="col-3 mb-2">
        <label class="mb-1">Model Developer</label>
        <select id="sel-dev-champion" class="selectpicker" multiple data-actions-box="true" data-width="100%" title="None selected" data-live-search="true"></select>
      </div>
    </div>
  `);

        const f = this.getFilters();
        const centers = f.Centers;
        const statuses = f.ModelStatuses;
        const champions = f.ModelChampions;

        const handle = this.asFilter(() => this.dtDeveloper);

        // Only initialize selects that actually exist in this tab:
        InitializeFilterDropdown('#sel-dev-center', centers, handle);
        InitializeFilterDropdown('#sel-dev-status', statuses, handle);
        InitializeFilterDropdown('#sel-dev-champion', champions, handle);

        // Build the union (Cores + Projects) grouped by Center for #sel-dev-core
        const centerOf = (m: AnimalModelVm) =>
            (m.Developer?.Center || m.Animal?.Center || m.Virus?.Center || '').trim();

        const coresProjectsByCenter: DropdownOptGroup[] = (() => {
            const map = new Map<string, Set<string>>();
            this.models.forEach(m => {
                const center = centerOf(m);
                if (!center) return;
                const add = (v?: string) => {
                    const s = (v || '').trim();
                    if (!s) return;
                    (map.get(center) || map.set(center, new Set()).get(center)!).add(s);
                };
                (m.Developer?.Cores || []).forEach(add);
                (m.Developer?.Projects || []).forEach(add);
            });

            return Array.from(map, ([label, set]) =>
                new DropdownOptGroup(label, Array.from(set).sort().map(v => [v, v])) // [value, text]
            );
        })();

        InitializeFilterOptGroupDropdown('#sel-dev-core', coresProjectsByCenter, handle, false);


        ['#sel-dev-center', /* '#sel-dev-core', */ '#sel-dev-status', '#sel-dev-champion']
            .forEach(id => $(id).attr('data-inited', '1'));
        // Install scoped search for Developer table
        this.removeSearch(this.developerSearchFn);
        this.developerSearchFn = (settings: any, _data: any[], dataIndex: number) => {
            if (settings.nTable?.id !== 'developerTable') return true;

            const api = new ($.fn.dataTable as any).Api(settings);
            const row = api.row(dataIndex).data() as AnimalModelVm;

            const selCenter = GetSelectedDropdownValues('#sel-dev-center') as string[] | null;
            const selCoreProj = GetSelectedDropdownValues('#sel-dev-core') as string[] | null;
            const selStatus = GetSelectedDropdownValues('#sel-dev-status') as string[] | null;
            const selChampion = GetSelectedDropdownValues('#sel-dev-champion') as string[] | null;

            // Center / Status / Champion use your standard helpers
            const okCenter = this.matchesOne(selCenter, '#sel-dev-center', row.Developer?.Center);
            const okStatus = this.matchesOne(selStatus, '#sel-dev-status', row.Developer?.ModelStatus);
            const okChampion = this.matchesOne(selChampion, '#sel-dev-champion', row.Developer?.Champion);

          
            const union = [...(row.Developer?.Cores || []), ...(row.Developer?.Projects || [])];
            const okCoreProj =
                !selCoreProj || selCoreProj.length === 0 || this.isAllSelected('#sel-dev-core', selCoreProj)
                    ? true
                    : DoArraysOverlap(union, selCoreProj);

            return okCenter && okCoreProj && okStatus && okChampion;
        };
        ($.fn.dataTable.ext.search as any[]).push(this.developerSearchFn);

        (this.dtDeveloper as any)?.draw(false);
        this.updateCountForActiveTab();
    }


    


    /////////////////////////////////////////////////////////////////////////
    // Start of Static page TS.------------------------------------------------------------------------------------------------------------------------
    // Initialize hover and click functionality for the responsive groups
    private init(): void {
        const groups = document.querySelectorAll<SVGGElement>('g.responsive-group');
        groups.forEach((group) => {
            this.addHoverEffect(group);
            this.addClickHandler(group);
        });
    }

    // Add hover effect to scale and bring Hexagon element to the top
    private addHoverEffect(group: SVGGElement): void {
        group.addEventListener('mouseenter', () => {
            this.resetAllGroups();
            group.style.transition = 'transform 0.3s ease, z-index 0s';
            group.style.transform = 'scale(1.02)';
            group.style.zIndex = '1000';
            group.style.cursor = 'pointer';
            group.style.filter = 'drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.2))';
            group.style.transformOrigin = 'center';
        });

        group.addEventListener('mouseleave', () => {
            group.style.transform = 'scale(1)';
            group.style.zIndex = '1';
            group.style.cursor = 'default';
            group.style.filter = 'none';
        });
    }

    // Click handler to open an external URL for Hexagons
    private addClickHandler(group: SVGGElement): void {
        group.addEventListener('click', () => {
            const externalUrl = group.getAttribute('data-url');
            if (externalUrl) {
                window.open(externalUrl, '_blank');
            }
        });
    }

    // Reset all groups to their original size and z-index ---> following hover
    private resetAllGroups(): void {
        const groups = document.querySelectorAll<SVGGElement>('g.responsive-group');
        groups.forEach((group) => {
            group.style.transform = 'scale(1)';
            group.style.zIndex = '1';
        });
    }

    // Toggle visibility of SVG and mobile list based on screen size
    private toggleVisibility(): void {
        const arrivePartialDiv = document.querySelector<HTMLDivElement>('.arrive-partial');
        const mobileList = document.getElementById('mobile-list');
        const isMobile = window.innerWidth <= 768;

        if (arrivePartialDiv && mobileList) {
            if (isMobile) {
                arrivePartialDiv.style.display = 'none';
                mobileList.style.display = 'block';
            } else {
                arrivePartialDiv.style.display = 'block';
                mobileList.style.display = 'none';
            }
        }
    }

    // Handle window resize to reapply the visibility logic dynamically
    private addResizeListener(): void {
        window.addEventListener('resize', () => {
            this.toggleVisibility();
        });
    }

    // Setup scroll highlight functionality for side navigation
    private setupScrollHighlight(): void {
        const navItems = $('.nav-items');

        // 16px between navbar and section top
        const EXTRA_GAP = 16;

        const getTopOffset = () => ($('.navbar:visible').outerHeight() || 0) + EXTRA_GAP;

        navItems.on('click', function (event) {
            event.preventDefault();
            const targetSel = $(this).attr('href');
            if (!targetSel || !targetSel.startsWith('#')) return;

            const $target = $(targetSel);
            if ($target.length === 0) return;

            const sectionTop = $target.offset()!.top - getTopOffset();
            $('html, body').animate({ scrollTop: sectionTop }, 400);

            navItems.removeClass('active');
            $(this).addClass('active');
        });

        $(window).on('scroll', () => {
            const scrollTopWithOffset = ($(window).scrollTop() || 0) + getTopOffset() + 1;

            navItems.each((_, item) => {
                const sel = $(item).attr('href');
                if (!sel || !sel.startsWith('#')) return;

                const $section = $(sel);
                if ($section.length === 0) return;

                const top = $section.offset()!.top;
                const height = $section.outerHeight() || 0;
                const bottom = top + height;

                const inViewFromTop = scrollTopWithOffset >= top && scrollTopWithOffset < bottom;
                if (inViewFromTop) {
                    navItems.removeClass('active');
                    $(item).addClass('active');
                }
            });
        });
    }


    // Initialize accordion toggle functionality
    public initAccordion(): void {
        const headers = document.querySelectorAll<HTMLDivElement>('.accordion-header');
        const readMoreElements = document.querySelectorAll<HTMLButtonElement>('.read-more');

        headers.forEach((header) => {
            header.addEventListener('click', () => this.toggleAccordion(header));
        });

        readMoreElements.forEach((readMore) => {
            readMore.addEventListener('click', (event) => {
                event.stopPropagation();
                const header = readMore.closest('.accordion-item')?.querySelector('.accordion-header') as HTMLDivElement;
                if (header) {
                    this.toggleAccordion(header, readMore);
                }
            });
        });
    }

    // Accordion toggle logic
    private toggleAccordion(header: HTMLDivElement, readMore?: HTMLButtonElement): void {
        const body = header.nextElementSibling as HTMLElement | null;
        if (!body) return;

        const isActive = header.classList.contains('active');

        if (!isActive) {
            header.classList.add('active');
            body.classList.add('show');

            const associatedReadMore = body.querySelector('.read-more') as HTMLButtonElement;
            if (associatedReadMore) {
                associatedReadMore.textContent = 'Show Less…';
            }
        } else {
            header.classList.remove('active');
            body.classList.remove('show');

            const associatedReadMore = body.querySelector('.read-more') as HTMLButtonElement;
            if (associatedReadMore) {
                associatedReadMore.textContent = 'Read More…';
            }
        }
    }
}
